import React, { useEffect, useState } from "react";
import {
  Chip,
  Typography,
  Box,
  IconButton,
  useMediaQuery,
  Tooltip,
} from "@mui/material";
import ReusabletableComponent from "../../../components/ReusabletableComponent";
import { BOOKING_FORM_STATUS } from "../../../config/constant";
import Header from "../../../layout/header/Header";
import { useHistory, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  getMultiBookingList,
  getTermsAndCondition,
  resetMultiBookingListData,
  setProjectBrandImage,
  setPartnerHeaderImage,
  setBrandLogo,
  setProjectId,
  setBrandId,
} from "../new-booking-form.slice";
import Loader from "../../../components/Loader";
import SnackbarAlert from "../../../shared-components/snackbar-alert/SnackbarAlert";
import linkIcon from "../../../assets/icons/linkicon.svg";

const MutliUnitBookingsTable = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const { groupId } = useParams();
  const history = useHistory();
  const dispatch = useDispatch();
  const DEBOUNCE_DELAY = 400;
  const isMobile = useMediaQuery("(max-width:600px)");

  const { isLoading, multiBookingListData } = useSelector(
    (state) => state.newBookingForm
  );
  const [snackbarConfig, setSnackbarConfig] = useState({
    open: false,
    message: "",
    severity: "",
  });
  const [searchText, setSearchText] = useState("");
  useEffect(() => {
    if (!groupId) return;

    const handler = setTimeout(() => {
      dispatch(
        getMultiBookingList({
          groupId,
          page: page + 1,
          limit: pageSize,
          search: searchText,
        })
      );
    }, DEBOUNCE_DELAY);

    return () => clearTimeout(handler);
  }, [searchText, page, pageSize, groupId, dispatch]);

  useEffect(() => {
    if (groupId) {
      dispatch(
        getMultiBookingList({ groupId, page: page + 1, limit: pageSize })
      );
    }
    return () => {
      dispatch(resetMultiBookingListData());
      dispatch(setProjectBrandImage(""));
      dispatch(setPartnerHeaderImage(""));
      dispatch(setBrandLogo(""));
    };
  }, [dispatch, groupId, page, pageSize]);

  /* Load header / background logos from terms (same CDN pattern as booking flow). */
  useEffect(() => {
    const projectName = (multiBookingListData?.opportunities?.[0]?.Project || "")
      .trim();
    if (!projectName || !groupId) return;

    let cancelled = false;
    (async () => {
      try {
        const termsData = await dispatch(
          getTermsAndCondition(`/${projectName}`)
        ).unwrap();
        if (cancelled || !termsData) return;
        if (termsData.projectId) dispatch(setProjectId(termsData.projectId));
        if (termsData.brandId) dispatch(setBrandId(termsData.brandId));
        dispatch(setProjectBrandImage(termsData.projectImage || ""));
        dispatch(setPartnerHeaderImage(termsData.jvPartnerLogo || ""));
        dispatch(setBrandLogo(termsData.brandLogo || ""));
      } catch {
        if (!cancelled) {
          dispatch(setProjectBrandImage(""));
          dispatch(setPartnerHeaderImage(""));
          dispatch(setBrandLogo(""));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    dispatch,
    groupId,
    multiBookingListData?.opportunities?.[0]?.Project,
  ]);

  const bookingList = multiBookingListData?.opportunities?.map((oppo) => ({
    ...oppo,
    id: oppo.Id, // map BE's Id to lowercase 'id'
  }));

  const totalRecords = multiBookingListData?.totalRecords || 0;

  const getBgColor = (status) => {
    switch (status) {
      case BOOKING_FORM_STATUS.NEW:
        return "#8E33FF29"; // Light purple
      case BOOKING_FORM_STATUS.IN_PROGRESS:
        return "#FFAB0029"; // warm yellow
      case BOOKING_FORM_STATUS.NOT_SIGNED:
        return "#FF563029"; // Light red
      case BOOKING_FORM_STATUS.SIGNED:
        return "#22C55E29"; // need to change
      case BOOKING_FORM_STATUS.SIGNED_OFFLINE:
        return "#22C55E29"; // Light green
      default:
        return "#00B8D929"; // Light blue
    }
  };

  const getTextColor = (status) => {
    switch (status) {
      case BOOKING_FORM_STATUS.NEW:
        return "#5119B7"; // purple
      case BOOKING_FORM_STATUS.IN_PROGRESS:
        return "#B76E00"; // yellow
      case BOOKING_FORM_STATUS.NOT_SIGNED:
        return "#B71D18"; // red
      case BOOKING_FORM_STATUS.SIGNED:
        return "#118D57"; // need to change
      case BOOKING_FORM_STATUS.SIGNED_OFFLINE:
        return "#118D57"; // green
      default:
        return "#006C9C"; // blue
    }
  };
  const isLinkDisabled = (row) => {

    return  (row?.status === BOOKING_FORM_STATUS.NEW || row?.status === BOOKING_FORM_STATUS.PRE_BOOKING_SUBMITTED) && multiBookingListData?.isFirstFormInProgress;
  };
  const columns = [
    { field: "Project", headerName: "Project Name", flex: 1, width: 150 },
    { field: "unitno", headerName: "Unit #", flex: 1.1, width: 150 },
    {
      field: "SalesValue",
      headerName: "Sale Value",
      flex: 0.8,
      width: 100,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "14px" }}>
          {params?.value ? Number(params?.value).toLocaleString("en-IN") : "-"}
        </Typography>
      ),
    },
    {
      field: "Amount",
      headerName: "Booking Amount",
      flex: 0.8,
      width: 150,
      renderCell: (params) => (
        <Typography sx={{ fontSize: "14px" }}>
          {params?.value ? Number(params?.value).toLocaleString("en-IN") : "-"}
        </Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1.3,
      width: 150,
      renderCell: (params) => (
        <Chip
          size="small"
          label={params.value}
          sx={{
            borderRadius: "6px",
            backgroundColor: getBgColor(params.value),
            color: getTextColor(params.value),
            fontWeight: 550,
            border: "none",
          }}
        />
      ),
    },
    {
      field: "Name",
      headerName: "Primary Applicant",
      flex: 1,
      width: 150,
    },
    {
      field: "Id",
      headerName: "Link",
      flex: 0.4,
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const disabled = isLinkDisabled(params.row);

        return (
          <Tooltip title="Copy Link" placement="top">
            <IconButton
              disabled={disabled}
              onClick={() => {
                if (disabled) return;
                handleCopyLink(params?.row);
              }}
              sx={{
                opacity: disabled ? 0.4 : 1,
                cursor: disabled ? "not-allowed" : "pointer",
              }}
            >
              <img
                src={linkIcon}
                alt="Copy Link"
                style={{ width: 22, height: 22 }}
              />
            </IconButton>
          </Tooltip>
        );
      },
    }
  ];

  const handleSnackbarClose = () => {
    setSnackbarConfig({
      open: false,
      message: "",
      severity: "",
    });
  };



  const handleCopyLink = (row) => {
    if (
      multiBookingListData?.isFirstFormFilled &&
      (row?.status === BOOKING_FORM_STATUS.NEW || row?.status === BOOKING_FORM_STATUS.PRE_BOOKING_SUBMITTED)
    ) {
      history.push(`/applicant-mapping/${groupId}?oppId=${row?.id}`);
    } else {
      history.push(`/booking-form/${row?.id}`);
    }
  };
  return (
    <>
      {isLoading && <Loader isLoading={isLoading} />}
      <Header showHamburger={false} />
      <Box sx={{ p: isMobile ? "16px" : "24px 80px 24px 80px" }}>
        <Typography sx={{ fontWeight: 600, fontSize: "22px", mb: 1 }}>
          Booking Details
        </Typography>
        <ReusabletableComponent
          columns={columns}
          rows={bookingList || []}
          searchPlaceholder="Search by Applicant Name, Project Name or Unit Number"
          loading={isLoading}
          rowCount={totalRecords}
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchText}
          searchText={searchText}
        />
      </Box>
      <SnackbarAlert
        open={snackbarConfig.open}
        message={snackbarConfig.message}
        severity={snackbarConfig.severity}
        onClose={handleSnackbarClose}
      />
    </>
  );
};

export default MutliUnitBookingsTable;
